import SEOHead from '@/components/SEOHead';
import Layout from '@/components/Layout';
import ArticlesConnexes from '@/components/ArticlesConnexes';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Calculator, TrendingUp, MapPin, Home, Users, Star, CheckCircle2, AlertCircle, DollarSign } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function EstimationImmobiliereLocation() {
  const navigate = useNavigate();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": "Estimation Immobilière Location Saisonnière : Le Guide Complet 2025",
    "description": "Comment estimer précisément le potentiel locatif saisonnier de votre bien ? Méthodes d'experts, critères d'évaluation, outils gratuits et simulation revenus réalistes pour tous types de biens en Suisse.",
    "image": "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=1200",
    "datePublished": "2025-01-11",
    "author": {
      "@type": "Organization",
      "name": "Homely Conciergerie"
    }
  };

  const criteresEvaluation = [
    {
      icon: MapPin,
      titre: "Localisation & Attractivité",
      poids: "35%",
      details: [
        "Proximité attractions touristiques (pistes ski, lac, vieille ville)",
        "Accessibilité (gare, aéroport, routes)",
        "Quartier sécurisé et dynamique",
        "Demande locative locale (données AirDNA, Booking)"
      ],
      impact: "+40% à +60% pour emplacements premium"
    },
    {
      icon: Home,
      titre: "Caractéristiques du Bien",
      poids: "30%",
      details: [
        "Surface habitable (m²)",
        "Nombre de chambres & capacité d'accueil",
        "État général & année de construction",
        "Équipements (cuisine, WiFi, parking, balcon, vue)"
      ],
      impact: "Vue panoramique = +25% tarif nuitée"
    },
    {
      icon: Star,
      titre: "Standing & Prestations",
      poids: "20%",
      details: [
        "Qualité mobilier et décoration",
        "Équipements premium (spa, cheminée, home cinema)",
        "Services inclus (linge, produits d'accueil)",
        "Note et avis précédents (si déjà en location)"
      ],
      impact: "Niveau luxe = x2 à x3 revenus"
    },
    {
      icon: Users,
      titre: "Marché Local & Saisonnalité",
      poids: "15%",
      details: [
        "Saisons touristiques (hiver ski, été lac)",
        "Événements locaux réguliers (festivals, foires)",
        "Concurrence directe (nombre d'annonces similaires)",
        "Taux d'occupation moyen zone"
      ],
      impact: "Haute saison : tarifs x2 à x3"
    }
  ];

  return (
    <Layout>
      <SEOHead
        title="Estimation Immobilière Location Saisonnière Suisse | Calculer Revenus Airbnb 2025"
        description="Guide complet pour estimer le potentiel locatif saisonnier de votre bien : méthodes d'experts, 10 critères clés, simulation revenus réalistes et outils gratuits. Estimation personnalisée sous 24h."
        keywords="estimation location saisonnière, calculer revenus airbnb, potentiel locatif suisse, évaluation bien immobilier, simulation revenus airbnb"
        canonical="https://homely-conciergerie.ch/blog/estimation-immobiliere-location-saisonniere"
        image="https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=1200"
        type="article"
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-[#F5F3EF]">
        {/* Hero */}
        <section 
          className="relative py-32 px-6 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(43, 43, 43, 0.75), rgba(43, 43, 43, 0.88)), url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=2000')`,
          }}
        >
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-[#8A9474] px-4 py-2 rounded-full text-white font-semibold mb-6">
              📊 Estimation & Évaluation
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Estimation Immobilière Location Saisonnière : Le Guide Complet
            </h1>

            <p className="text-xl text-gray-200 leading-relaxed mb-8">
              <strong>Combien rapporte VRAIMENT votre bien en location saisonnière ?</strong> Découvrez la méthode en 5 étapes des experts pour estimer précisément vos revenus potentiels et éviter les erreurs coûteuses.
            </p>

            <div className="flex items-center justify-center gap-6 text-white/90">
              <span>📅 11 janvier 2025</span>
              <span>•</span>
              <span>⏱ 15 min</span>
            </div>
          </div>
        </section>

        {/* Introduction captivante */}
        <article className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none mb-16">
              <p className="text-xl text-[#4A4A4A] leading-relaxed font-serif italic border-l-4 border-[#8A9474] pl-6 mb-8">
                "Quand j'ai acheté mon appartement à Montreux, l'agent immobilier m'a dit : 'Vous pouvez espérer 3'000 CHF/mois en location classique'. Aujourd'hui, grâce à la location saisonnière optimisée, je génère <strong>8'200 CHF/mois</strong> en moyenne annuelle. L'estimation initiale était sous-évaluée de 173%."
                <span className="block mt-2 text-base not-italic text-[#8A9474]">— Claire D., propriétaire Montreux</span>
              </p>

              <p className="text-lg text-[#4A4A4A] leading-relaxed mb-6">
                L'estimation immobilière pour la location saisonnière est <strong>radicalement différente</strong> d'une évaluation locative traditionnelle. Les critères, les méthodes de calcul, et surtout le <strong>potentiel de revenus</strong> n'ont rien à voir.
              </p>

              <p className="text-lg text-[#4A4A4A] leading-relaxed">
                Dans ce guide ultra-complet, je vais vous révéler exactement comment les professionnels évaluent un bien pour la location saisonnière, quels sont les 10 critères décisifs, et comment obtenir une estimation fiable en moins de 5 minutes.
              </p>
            </div>

            {/* Alerte Important */}
            <Card className="bg-[#8A9474]/10 border-2 border-yellow-400 mb-16">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <AlertCircle className="h-8 w-8 text-[#6B6B6B] flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-bold text-[#2B2B2B] mb-3">⚠️ Erreur Fatale à Éviter</h3>
                    <p className="text-[#4A4A4A] mb-3">
                      <strong>Ne vous fiez JAMAIS à une estimation basée uniquement sur :</strong>
                    </p>
                    <ul className="space-y-2 text-[#4A4A4A]">
                      <li>❌ Le loyer mensuel classique × 12</li>
                      <li>❌ Les "calculateurs automatiques" génériques en ligne</li>
                      <li>❌ L'avis d'un agent immobilier traditionnel</li>
                      <li>❌ Les revenus d'un voisin sans connaître ses détails</li>
                    </ul>
                    <p className="text-[#4A4A4A] mt-3">
                      Ces méthodes sous-estiment ou surestiment systématiquement de <strong>30% à 50%</strong> votre potentiel réel.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Méthode en 5 Étapes */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-8 text-center">
                La Méthode Professionnelle en 5 Étapes
              </h2>

              <div className="space-y-6">
                <Card className="border-l-4 border-[#8A9474]">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-[#8A9474] text-white rounded-full flex items-center justify-center font-bold text-xl">
                        1
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-[#2B2B2B] mb-3">Analyse du Marché Local</h3>
                        <p className="text-[#4A4A4A] mb-3">
                          Étudiez les annonces similaires actives dans votre zone (même quartier, capacité, standing) :
                        </p>
                        <ul className="space-y-2 text-[#4A4A4A] text-sm">
                          <li>• <strong>Airbnb :</strong> recherchez votre ville + filtres (nb chambres, équipements)</li>
                          <li>• <strong>Booking.com :</strong> vérifiez les tarifs d'appartements comparables</li>
                          <li>• <strong>AirDNA :</strong> données marché précises (payant mais fiable)</li>
                          <li>• Notez : prix/nuit hiver, été, taux occupation, nb avis</li>
                        </ul>
                        <div className="mt-4 p-3 bg-[#8A9474]/10 rounded">
                          <p className="text-sm text-[#4A4A4A]">
                            <strong>📍 Exemple Lausanne :</strong> Appart 3 pièces centre = 180-280 CHF/nuit selon saison
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-[#8A9474]">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-[#E8EBE4]0 text-white rounded-full flex items-center justify-center font-bold text-xl">
                        2
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-[#2B2B2B] mb-3">Évaluation des 4 Critères Majeurs</h3>
                        <p className="text-[#4A4A4A] mb-3">
                          Positionnez votre bien sur ces 4 axes (détail plus bas) :
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          {criteresEvaluation.map((critere, idx) => {
                            const Icon = critere.icon;
                            return (
                              <div key={idx} className="bg-gray-50 p-3 rounded">
                                <div className="flex items-center gap-2 mb-1">
                                  <Icon className="h-5 w-5 text-[#8A9474]" />
                                  <span className="font-bold text-sm">{critere.titre}</span>
                                </div>
                                <span className="text-xs text-[#4A4A4A]">Poids : {critere.poids}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-green-500">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-xl">
                        3
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-[#2B2B2B] mb-3">Calcul du Prix Moyen Nuitée</h3>
                        <p className="text-[#4A4A4A] mb-3">
                          Formule ajustée en fonction de vos critères :
                        </p>
                        <div className="bg-gray-900 text-white p-4 rounded font-mono text-sm mb-3">
                          Prix Moyen Nuitée = (Prix Marché Local) × (1 + Bonus Standing) × (1 + Bonus Localisation)
                        </div>
                        <p className="text-sm text-[#4A4A4A] mb-2"><strong>Exemple pratique :</strong></p>
                        <ul className="text-sm text-[#4A4A4A] space-y-1">
                          <li>• Prix marché Lausanne 3p : 220 CHF/nuit</li>
                          <li>• Bonus standing (+15% car rénové récent, déco soignée)</li>
                          <li>• Bonus localisation (+10% car centre piétonnier)</li>
                          <li>• <strong>Prix optimal = 220 × 1.15 × 1.10 = 278 CHF/nuit</strong></li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-[#8A9474]">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-[#E8EBE4]0 text-white rounded-full flex items-center justify-center font-bold text-xl">
                        4
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-[#2B2B2B] mb-3">Projection du Taux d'Occupation</h3>
                        <p className="text-[#4A4A4A] mb-3">
                          Le taux d'occupation dépend de votre gestion et localisation :
                        </p>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between items-center bg-[#8A9474]/10 p-2 rounded">
                            <span className="text-[#4A4A4A]">😐 Gestion amateur, pas de pro</span>
                            <span className="font-bold text-[#2B2B2B]">50-65%</span>
                          </div>
                          <div className="flex justify-between items-center bg-[#8A9474]/10 p-2 rounded">
                            <span className="text-[#4A4A4A]">🙂 Gestion déléguée, prix manuel</span>
                            <span className="font-bold text-yellow-700">70-80%</span>
                          </div>
                          <div className="flex justify-between items-center bg-green-50 p-2 rounded">
                            <span className="text-[#4A4A4A]">🌟 Conciergerie pro + pricing IA</span>
                            <span className="font-bold text-green-700">85-95%</span>
                          </div>
                        </div>
                        <p className="text-xs text-[#4A4A4A] mt-3">
                          💡 Homely Conciergerie atteint en moyenne <strong>91% de taux d'occupation</strong> grâce au pricing dynamique et photos pro.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-[#8A9474]">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-[#8A9474] text-white rounded-full flex items-center justify-center font-bold text-xl">
                        5
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-[#2B2B2B] mb-3">Calcul Final Revenus Annuels</h3>
                        <div className="bg-gradient-to-br from-[#2B2B2B] to-[#4A4A4A] text-white p-6 rounded-lg mb-3">
                          <p className="text-lg mb-4 font-bold">📐 Formule Complète :</p>
                          <div className="bg-white/10 p-4 rounded font-mono text-sm">
                            Revenus Annuels Bruts =<br/>
                            (Prix Moyen Nuitée) × (365 jours) × (Taux Occupation) × (Ajustement Saisonnier)
                          </div>
                        </div>
                        <div className="bg-[#8A9474]/10 p-4 rounded">
                          <p className="font-bold text-[#2B2B2B] mb-2">🧮 Exemple Complet (Lausanne 3p) :</p>
                          <ul className="text-sm text-[#4A4A4A] space-y-1">
                            <li>• Prix moyen : 278 CHF/nuit (calculé étape 3)</li>
                            <li>• Taux occupation : 88% (gestion pro)</li>
                            <li>• Ajustement saisonnier : 0.95 (variations été/hiver)</li>
                            <li>• <strong className="text-[#8A9474]">= 278 × 365 × 0.88 × 0.95 = 84'720 CHF/an</strong></li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Détail des 4 Critères */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-[#2B2B2B] mb-8 text-center">
                Les 4 Critères d'Évaluation en Détail
              </h2>

              <div className="space-y-8">
                {criteresEvaluation.map((critere, index) => {
                  const Icon = critere.icon;
                  return (
                    <Card key={index} className="overflow-hidden">
                      <CardContent className="p-8">
                        <div className="flex items-start gap-6">
                          <div className="flex-shrink-0 w-16 h-16 bg-[#8A9474]/10 rounded-full flex items-center justify-center">
                            <Icon className="h-8 w-8 text-[#8A9474]" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-2xl font-bold text-[#2B2B2B]">{critere.titre}</h3>
                              <span className="bg-[#8A9474] text-white px-4 py-1 rounded-full text-sm font-bold">
                                Poids {critere.poids}
                              </span>
                            </div>
                            <ul className="space-y-2 mb-4">
                              {critere.details.map((detail, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-[#4A4A4A]">
                                  <CheckCircle2 className="h-5 w-5 text-[#8A9474] flex-shrink-0 mt-0.5" />
                                  <span>{detail}</span>
                                </li>
                              ))}
                            </ul>
                            <div className="bg-[#E8EBE4] border-l-4 border-[#8A9474] p-4 rounded">
                              <p className="text-[#4A4A4A]">
                                <strong>💰 Impact revenus :</strong> {critere.impact}
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Simulateur Simplifié */}
            <Card className="bg-gradient-to-br from-[#2B2B2B] to-[#4A4A4A] text-white mb-16">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <Calculator className="h-16 w-16 mx-auto mb-4" />
                  <h2 className="text-3xl font-bold mb-3">Simulateur Express</h2>
                  <p className="text-gray-100">
                    Estimation rapide basée sur les moyennes de marché Suisse romande 2025
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg text-center">
                    <p className="text-gray-200 text-sm mb-2">Studio 2 pers</p>
                    <p className="text-4xl font-bold mb-2">38'000</p>
                    <p className="text-gray-200 text-sm">CHF/an moyen</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg text-center">
                    <p className="text-gray-200 text-sm mb-2">Appart 3-4 pièces</p>
                    <p className="text-4xl font-bold mb-2">78'000</p>
                    <p className="text-gray-200 text-sm">CHF/an moyen</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm p-6 rounded-lg text-center">
                    <p className="text-gray-200 text-sm mb-2">Chalet 6-8 pers</p>
                    <p className="text-4xl font-bold mb-2">125'000</p>
                    <p className="text-gray-200 text-sm">CHF/an moyen</p>
                  </div>
                </div>

                <p className="text-center text-gray-100 text-sm mt-6">
                  ⚠️ Estimations indicatives. Variation ±30% selon localisation précise et standing.
                </p>
              </CardContent>
            </Card>

            {/* CTA Estimation Gratuite */}
            <Card className="bg-gradient-to-br from-[#2B2B2B] to-[#4A4A4A] text-white overflow-hidden">
              <CardContent className="p-12 text-center">
                <DollarSign className="h-16 w-16 mx-auto mb-6 text-[#8A9474]" />
                <h2 className="text-3xl font-bold mb-4">
                  Obtenez Votre Estimation Personnalisée Gratuite
                </h2>
                <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
                  Nos experts analysent votre bien en détail et vous fournissent une <strong>projection de revenus ultra-précise</strong> sous 24h. Zéro engagement.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-[#8A9474]" />
                    <span>Gratuit & sans engagement</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-[#8A9474]" />
                    <span>Réponse sous 24h</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-[#8A9474]" />
                    <span>Stratégie personnalisée</span>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/estimation')}
                  size="lg"
                  className="bg-[#8A9474] hover:bg-[#7A8464] text-white px-12 py-7 text-lg font-semibold"
                >
                  Estimer Mon Bien Maintenant
                </Button>
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="mt-12 pt-8 border-t-2 border-gray-200">
              <Button 
                onClick={() => navigate('/blog')}
                variant="outline"
                className="border-[#8A9474] text-[#8A9474] hover:bg-[#8A9474] hover:text-white"
              >
                ← Retour au Blog
              </Button>
            </div>
          </div>
        </article>
      </div>
    </Layout>
  );
}
